import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getGlobalApiEndpoint,
  setApiCompany,
  clearApiCompany,
  clearAdminCompanyCode,
  getApiCompany,
} from "../config/config";
import { isProtectedRoute } from "../config/routes";
import { signOut as clearGoogleCalendarToken } from "../services/googleCalendar";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Helper function to check if JWT token is expired
  const isTokenExpired = token => {
    try {
      if (!token) return true;

      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true; // Consider token expired if we can't parse it
    }
  };

  // Decode a JWT payload (unverified) so we can read claims like the per-module
  // access flags. Returns {} if the token can't be parsed.
  const decodeJwtPayload = token => {
    try {
      if (!token) return {};
      return JSON.parse(atob(token.split(".")[1]));
    } catch (error) {
      return {};
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (user && user.jwt && !isTokenExpired(user.jwt)) {
          // setUser(userData);
          setAuthLoading(false);
          return;
        }

        try {
          const response = await fetch(getGlobalApiEndpoint("/v1/user/token"), {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (response.ok) {
            const result = await response.json();
            const data = result.data;
            // Login user with the response data
            login(data);
          } else {
            logout(true);
            const currentPath = window.location.pathname;
            if (isProtectedRoute(currentPath)) {
              window.location.href = "/";
            }
          }
        } catch (error) {
          logout(true);
          const currentPath = window.location.pathname;
          if (isProtectedRoute(currentPath)) {
            window.location.href = "/";
          }
        }
      } catch (error) {
        logout(true);
      } finally {
        setAuthLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async userData => {
    const claims = decodeJwtPayload(userData.jwt);

    // The JWT carries the company that every API call is routed through.
    // Store it before anything else so later requests hit the right host.
    const company = userData.company ?? claims.company;
    if (company) {
      setApiCompany(company);
    }

    const userObj = {
      // The company code string this login is scoped to. Empty when the token
      // carries no company, which is what the company-assignment gate checks.
      company: company ? getApiCompany() : "",
      firstName: userData.first_name,
      lastName: userData.last_name,
      repId: userData.rep_id,
      role: userData.role,
      jwt: userData.jwt,
      verified: userData.verified,
      email: userData.email,
      // Admins aren't tied to a single company, so they skip the company
      // assignment gate even without a company code.
      isAdmin: userData.is_admin ?? claims.is_admin ?? false,
      managerRepId: userData.manager_rep_id,
      permissions: userData.permissions || [],
      profileImageUrl: userData.profile_image_url || null,
      // Per-module access flags from the JWT (e.g. { general_suite, welcome_survey }).
      // Falls back to full general-suite access for older tokens that predate the claim.
      modules: userData.modules ??
        claims.modules ?? { general_suite: true, welcome_survey: false },
    };
    setUser(userObj);
    if (userData.chat_access_token) {
      //connect Agora
    }

    // Load Google Calendar token if exists (but don't let it break login)
    if (userData.jwt) {
      try {
        // await loadTokenFromBackend(userData.jwt);
      } catch (error) {
        // Don't throw - calendar token is optional
      }
    }
  };

  const logout = async (skipRedirect = false) => {
    // Get JWT directly from user state to avoid circular dependency
    let jwt = user?.jwt;

    if (jwt && isTokenExpired(jwt)) {
      try {
        // Attempt to get a new token
        const response = await fetch(getGlobalApiEndpoint("/v1/user/token"), {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          const userData = result.data;

          jwt = userData.jwt;
        } else {
          jwt = null;
        }
      } catch (error) {
        jwt = null;
      }
    }

    if (jwt && !isTokenExpired(jwt)) {
      try {
        await fetch(getGlobalApiEndpoint("/v1/user/logout"), {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        });
      } catch (error) {
        // Ignore errors during logout API call
      }
    }

    // Always clear user state and disconnect from chat
    setUser(null);
    clearApiCompany();
    clearAdminCompanyCode();
    clearGoogleCalendarToken();

    if (
      !skipRedirect &&
      window.location.pathname !== "/" &&
      window.location.pathname !== "/home"
    ) {
      window.location.href = "/login";
    }
  };

  const isAuthenticated = () => {
    return user !== null;
  };

  // Global JWT verification function
  // Returns the JWT if valid, attempts to refresh if expired, otherwise logs the user out and returns null
  const getVerifiedJWT = useCallback(async () => {
    // Check if user exists and has a JWT
    if (!user || !user.jwt) {
      await logout();
      return null;
    }

    // Check if JWT is expired
    if (isTokenExpired(user.jwt)) {
      try {
        // Attempt to get a new token
        const response = await fetch(getGlobalApiEndpoint("/v1/user/token"), {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          const userData = result.data;
          // Update user with new JWT
          login(userData);
          return userData.jwt;
        } else {
          await logout();
          return null;
        }
      } catch (error) {
        await logout();
        return null;
      }
    }

    // JWT is valid, return it
    return user.jwt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Function to update specific user properties
  const updateUser = useCallback(updates => {
    setUser(prevUser => ({
      ...prevUser,
      ...updates,
    }));
  }, []);

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    authLoading,
    getVerifiedJWT,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {authLoading ? null : children}
    </AuthContext.Provider>
  );
};
